import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import CapitalEventDateForm from "features/capital-activity/event/CapitalEventDateForm";
import { useFundFamilyEventCreate } from "gen/endpoints/fund-family/fund-family";
import { FundFamilyEventData } from "gen/model/fundFamilyEventData";
import { FundFamilyEventTypeEnum } from "gen/model/fundFamilyEventTypeEnum";
import { fundFamilyEventCreateBody } from "gen/schemas/fund-family/fund-family";
import React, { useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ButtonOrder } from "shared/components/button/ButtonOrder";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import Form from "shared/components/form/Form";
import { FormTextArea } from "shared/components/input/text/TextArea";
import { nowAsIsoDate } from "shared/utils/DateUtils";
import { useBoolean } from "usehooks-ts";

interface AddCapitalEventModalProps {
  onCloseComplete: () => void;
  onSuccess: (fundFamilyEventId: string) => void;
  fund_family_id: string;
}

const AddCapitalEvent: React.FC<AddCapitalEventModalProps> = ({
  onSuccess,
  onCloseComplete,
  fund_family_id,
}) => {
  const { t } = useTranslation(["capitalActivity", "common"]);

  const cancelRef = useRef(null);

  const { value: isOpen, setFalse: close } = useBoolean(true);

  const form = useForm<
    FundFamilyEventData & { different_capital_call_date: boolean }
  >({
    resolver: zodResolver(fundFamilyEventCreateBody),
    defaultValues: {
      fund_family_id,
      type: FundFamilyEventTypeEnum.capital_activity,
      effective_date: nowAsIsoDate(),
      posted_date: nowAsIsoDate(),
      capital_call_effective_date: null,
      capital_call_posted_date: null,
      different_capital_call_date: false,
    },
  });

  const { mutateAsync: createFundFamilyEvent } = useFundFamilyEventCreate();

  const onSubmit = async (data: FundFamilyEventData) =>
    await createFundFamilyEvent({
      fundFamilyId: fund_family_id,
      data,
    });

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      onCloseComplete={onCloseComplete}
      initialFocusRef={cancelRef}
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t("capitalActivity:runCapitalEventModal.header")}
        </ModalHeader>
        <Form
          form={form}
          onSubmit={onSubmit}
          onSuccess={(responseData) => {
            onSuccess(responseData.id!);
            close();
          }}
        >
          <ModalBody>
            <Text marginBottom={4} fontSize={14} fontWeight={400}>
              {t("capitalActivity:runCapitalEventModal.description")}
            </Text>
            <CapitalEventDateForm />
            <FormTextArea
              name="notes"
              title={t("capitalActivity:runCapitalEventModal.notes")}
            />
          </ModalBody>
          <ModalFooter gap={2}>
            <ButtonOrder
              dismiss={<FormCancelButton onClick={close} ref={cancelRef} />}
              confirm={
                <FormSubmitButton>{t("common:continue")}</FormSubmitButton>
              }
            />
          </ModalFooter>
        </Form>
      </ModalContent>
    </Modal>
  );
};

export default AddCapitalEvent;
